import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Grid,
  IconButton,
  Paper,
  Switch,
  Tooltip,
  Typography,
} from '@mui/material';
import { Add, ArrowBack, Edit, Delete } from '@mui/icons-material';
import useSnackbar from '../../../hooks/useSnackbar';
import { getCatalog, SbCatalog } from '../../../services/serviceBooking';
import { useCatalogEditor } from './useCatalogEditor';

const EMPTY: SbCatalog = { jobTypes: [], appliances: [], components: [], attributeOptions: [] };
const PURPLE = '#A855F7';
const AMBER = '#E0A82E';

const SectionHeader: React.FC<{ title: string; hint?: string; action?: React.ReactNode }> = ({
  title,
  hint,
  action,
}) => (
  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 3, mb: 1.5 }}>
    <Box>
      <Typography sx={{ fontSize: '1rem', fontWeight: 700, color: '#fff' }}>{title}</Typography>
      {hint && <Typography sx={{ color: '#8A8D91', fontSize: '0.82rem' }}>{hint}</Typography>}
    </Box>
    {action}
  </Box>
);

const EnquiryDetail: React.FC = () => {
  const { key = '' } = useParams<{ key: string }>();
  const navigate = useNavigate();
  const { showSnackbar, SnackbarComponent } = useSnackbar();
  const [catalog, setCatalog] = useState<SbCatalog>(EMPTY);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const data = await getCatalog();
      setCatalog({ ...EMPTY, ...data });
    } catch (err: any) {
      showSnackbar(err?.message || 'Failed to load', 'error');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const { openCreate, openEdit, quickToggle, handleDelete, editorElement } = useCatalogEditor(
    catalog,
    load,
    showSnackbar,
  );

  const jobType = catalog.jobTypes.find((j) => j.key === key) || null;
  const isPrimary = jobType?.group !== 'secondary';
  const appliances = (catalog.appliances || []).filter(
    (a) => !a.supportedJobTypeKeys?.length || a.supportedJobTypeKeys.includes(key),
  );
  const acTypes = (catalog.attributeOptions || []).filter((o) => o.listKey === 'ac_type');
  const problems = (catalog.attributeOptions || []).filter((o) => o.listKey === 'problem_list');
  const otherComponent =
    (catalog.components || []).find((c) => c.triggersIntakeForm) || null;

  if (loading) {
    return (
      <Box sx={{ p: 8, textAlign: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!jobType) {
    return (
      <Box sx={{ p: 4 }}>
        <Button startIcon={<ArrowBack />} onClick={() => navigate('/manage/services')}>
          Back
        </Button>
        <Typography sx={{ mt: 3, color: 'text.secondary' }}>Enquiry type not found.</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, width: '100%' }}>
      <Button
        startIcon={<ArrowBack />}
        onClick={() => navigate('/manage/services')}
        sx={{ textTransform: 'none', color: '#bbb', mb: 2 }}
      >
        Back to Services
      </Button>

      {/* ── Header ─────────────────────────────────────────────────────── */}
      <Paper
        variant="outlined"
        sx={{
          p: 2.5,
          borderRadius: '14px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 2,
          background: 'linear-gradient(135deg,rgba(123,31,162,0.12),rgba(168,85,247,0.04))',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Avatar
            src={jobType.icon || undefined}
            variant="rounded"
            sx={{ width: 56, height: 56, bgcolor: 'rgba(168,85,247,0.2)', color: PURPLE, fontWeight: 700, fontSize: '1.4rem' }}
          >
            {jobType.label?.[0]}
          </Avatar>
          <Box>
            <Typography sx={{ fontSize: '1.5rem', fontWeight: 800 }}>{jobType.label}</Typography>
            <Chip
              size="small"
              label={isPrimary ? 'Booking-based enquiry' : 'Request-based enquiry'}
              color={isPrimary ? 'primary' : 'info'}
              sx={{ mt: 0.5, fontWeight: 600 }}
            />
          </Box>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Tooltip title={jobType.isActive === false ? 'Disabled' : 'Enabled'}>
            <Switch
              checked={jobType.isActive !== false}
              onChange={(e) => quickToggle('jobTypes', jobType, e.target.checked)}
            />
          </Tooltip>
          <Button variant="outlined" startIcon={<Edit />} onClick={() => openEdit('jobTypes', jobType)}>
            Edit
          </Button>
        </Box>
      </Paper>

      <Typography sx={{ color: '#8A8D91', fontSize: '0.9rem', mt: 2 }}>
        {isPrimary
          ? 'Customers pick this enquiry, then an appliance, then a component (or “Other” to open the intake form). Manage that whole flow below.'
          : 'A request-based action with a separate flow — no appliances or components to configure.'}
      </Typography>

      {!isPrimary && (
        <Box sx={{ mt: 3, p: 3, borderRadius: '12px', border: '1px dashed rgba(255,255,255,0.15)', color: '#8A8D91' }}>
          Nothing to configure here. Use <b>Edit</b> to change this enquiry’s name, icon, or status.
        </Box>
      )}

      {isPrimary && (
        <>
          {/* ── Appliances & Components ────────────────────────────────── */}
          <SectionHeader
            title="Appliances & Components"
            hint="Step 2-3 · equipment offered under this enquiry and their component pools"
            action={
              <Button
                variant="contained"
                size="small"
                startIcon={<Add />}
                onClick={() => openCreate('appliances', { supportedJobTypeKeys: [key] })}
              >
                Add equipment
              </Button>
            }
          />

          {appliances.length === 0 ? (
            <Paper variant="outlined" sx={{ p: 3, textAlign: 'center', color: 'text.secondary', borderRadius: '12px' }}>
              No equipment yet. Click “Add equipment” to start building this flow.
            </Paper>
          ) : (
            <Grid container spacing={2}>
              {appliances.map((a) => {
                const comps = (catalog.components || []).filter(
                  (c) => c.applianceKeys?.includes(a.key) && !c.triggersIntakeForm,
                );
                return (
                  <Grid item xs={12} md={6} key={a.key}>
                    <Paper
                      variant="outlined"
                      sx={{ p: 2, borderRadius: '12px', height: '100%', opacity: a.isActive === false ? 0.55 : 1 }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2 }}>
                          <Avatar
                            src={a.icon || undefined}
                            variant="rounded"
                            sx={{ width: 36, height: 36, bgcolor: 'rgba(168,85,247,0.18)', color: PURPLE, fontWeight: 700 }}
                          >
                            {a.label?.[0]}
                          </Avatar>
                          <Box>
                            <Typography sx={{ fontWeight: 700 }}>{a.label}</Typography>
                            {a.description && (
                              <Typography sx={{ color: '#8A8D91', fontSize: '0.75rem' }}>{a.description}</Typography>
                            )}
                          </Box>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <Switch
                            size="small"
                            checked={a.isActive !== false}
                            onChange={(e) => quickToggle('appliances', a, e.target.checked)}
                          />
                          <IconButton size="small" onClick={() => openEdit('appliances', a)} sx={{ color: PURPLE }}>
                            <Edit fontSize="small" />
                          </IconButton>
                          <IconButton size="small" onClick={() => handleDelete('appliances', a)} sx={{ color: '#ff5c6c' }}>
                            <Delete fontSize="small" />
                          </IconButton>
                        </Box>
                      </Box>

                      <Divider sx={{ my: 1.5 }} />

                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, alignItems: 'center' }}>
                        {comps.length === 0 && (
                          <Typography sx={{ color: '#666', fontSize: '0.8rem', mr: 1 }}>No components yet</Typography>
                        )}
                        {comps.map((c) => (
                          <Chip
                            key={c.key}
                            avatar={
                              <Avatar
                                src={c.icon || undefined}
                                sx={{ bgcolor: 'rgba(168,85,247,0.25)', color: `${PURPLE} !important`, fontWeight: 700 }}
                              >
                                {c.label?.[0]}
                              </Avatar>
                            }
                            label={`${c.label}${c.basePrice ? ` · £${c.basePrice}` : ''}`}
                            onClick={() => openEdit('components', c)}
                            onDelete={() => handleDelete('components', c)}
                            size="medium"
                            sx={{
                              bgcolor: c.isActive === false ? '#2a2a2a' : '#2d2440',
                              color: c.isActive === false ? '#777' : '#e6d9ff',
                              border: '1px solid rgba(168,85,247,0.3)',
                              borderRadius: '8px',
                            }}
                          />
                        ))}
                        <Button
                          size="small"
                          startIcon={<Add />}
                          onClick={() => openCreate('components', { applianceKeys: [a.key] })}
                          sx={{ textTransform: 'none', color: '#8A8D91', fontSize: '0.78rem' }}
                        >
                          Add component
                        </Button>
                      </Box>
                    </Paper>
                  </Grid>
                );
              })}
            </Grid>
          )}

          {/* ── Other → Intake form ────────────────────────────────────── */}
          <SectionHeader
            title="“Other” → Intake form"
            hint="Step 4 · shown when the customer picks “Other”. Shared across all equipment."
            action={
              !otherComponent && (
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<Add />}
                  onClick={() =>
                    openCreate('components', {
                      label: 'Other',
                      triggersIntakeForm: true,
                      applianceKeys: appliances.map((a) => a.key),
                    })
                  }
                >
                  Add “Other” option
                </Button>
              )
            }
          />

          {otherComponent && (
            <Paper
              variant="outlined"
              sx={{
                p: 2,
                mb: 2,
                borderRadius: '12px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 2,
                border: '1px solid rgba(224,168,46,0.3)',
                background: 'rgba(224,168,46,0.05)',
                opacity: otherComponent.isActive === false ? 0.55 : 1,
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Avatar
                  src={otherComponent.icon || undefined}
                  variant="rounded"
                  sx={{ width: 44, height: 44, bgcolor: 'rgba(224,168,46,0.16)', color: AMBER, fontWeight: 700 }}
                >
                  {otherComponent.label?.[0]}
                </Avatar>
                <Box>
                  <Typography sx={{ fontWeight: 700 }}>{otherComponent.label}</Typography>
                  <Typography sx={{ color: '#8A8D91', fontSize: '0.78rem' }}>
                    Shown as the last option on every equipment — opens the intake form below
                  </Typography>
                </Box>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Switch
                  size="small"
                  checked={otherComponent.isActive !== false}
                  onChange={(e) => quickToggle('components', otherComponent, e.target.checked)}
                />
                <IconButton
                  size="small"
                  onClick={() => openEdit('components', otherComponent)}
                  sx={{ color: AMBER }}
                >
                  <Edit fontSize="small" />
                </IconButton>
              </Box>
            </Paper>
          )}
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Paper variant="outlined" sx={{ p: 2, borderRadius: '12px' }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                  <Typography sx={{ fontWeight: 700, color: AMBER }}>Appliance / part type list</Typography>
                  <Button
                    size="small"
                    startIcon={<Add />}
                    onClick={() => openCreate('attributeOptions', { listKey: 'ac_type' })}
                    sx={{ textTransform: 'none', color: AMBER }}
                  >
                    Add
                  </Button>
                </Box>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                  {acTypes.length === 0 && <Typography sx={{ color: '#666', fontSize: '0.8rem' }}>None yet</Typography>}
                  {acTypes.map((o) => (
                    <Chip
                      key={o.value}
                      label={o.label}
                      onClick={() => openEdit('attributeOptions', o)}
                      size="small"
                      sx={{
                        bgcolor: o.isActive === false ? '#2a2a2a' : 'rgba(224,168,46,0.14)',
                        color: o.isActive === false ? '#777' : '#f0c869',
                        border: '1px solid rgba(224,168,46,0.3)',
                        borderRadius: '8px',
                      }}
                    />
                  ))}
                </Box>
              </Paper>
            </Grid>
            <Grid item xs={12} md={6}>
              <Paper variant="outlined" sx={{ p: 2, borderRadius: '12px' }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                  <Typography sx={{ fontWeight: 700, color: AMBER }}>Problem list</Typography>
                  <Button
                    size="small"
                    startIcon={<Add />}
                    onClick={() => openCreate('attributeOptions', { listKey: 'problem_list' })}
                    sx={{ textTransform: 'none', color: AMBER }}
                  >
                    Add
                  </Button>
                </Box>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                  {problems.length === 0 && <Typography sx={{ color: '#666', fontSize: '0.8rem' }}>None yet</Typography>}
                  {problems.map((o) => (
                    <Chip
                      key={o.value}
                      label={o.label}
                      onClick={() => openEdit('attributeOptions', o)}
                      size="small"
                      sx={{
                        bgcolor: o.isActive === false ? '#2a2a2a' : 'rgba(224,168,46,0.14)',
                        color: o.isActive === false ? '#777' : '#f0c869',
                        border: '1px solid rgba(224,168,46,0.3)',
                        borderRadius: '8px',
                      }}
                    />
                  ))}
                </Box>
              </Paper>
            </Grid>
          </Grid>
        </>
      )}

      {editorElement}
      {SnackbarComponent}
    </Box>
  );
};

export default EnquiryDetail;
